"use client";

import React from "react";
import { Journal } from "@/types/journal";
import { I18nData } from "@/types/i18n";
import { Branding } from "@/types/branding";
import { useLocalizedData, useLocale } from "@/context/LocaleContext";
import { useSearch } from "@/context/SearchContext";
import CollapsibleRow from "./collapsible-row";
import MultiLicenseCell from "./multi-license-cell";
import LicenseCell from "./license-cell";
import Icon from "@/components/common/Icon";
import { generatePaymentLink, generatePublishingModelLink, generateJournalLink } from "@/utils/links";

interface Props {
  journal: Journal;
  i18nData: I18nData;
  branding: Branding | null;
}

const Row = React.memo(function Row({ journal, i18nData, branding }: Props) {
  const localizedData = useLocalizedData(i18nData);
  const { locale } = useLocale();
  const { slug } = useSearch();
  
  // Get display data
  const paymentIcon = journal.payment ? localizedData?.payments?.[journal.payment] : undefined;
  const licenseKeys = journal.licenses || [];
  const licenseIcons = journal.licenses?.map(
    (license) => localizedData?.licenses?.[license]
  ).filter((icon): icon is NonNullable<typeof icon> => Boolean(icon)) || [];
  const publishingModelIcon = journal.publishing_model 
    ? localizedData?.publishing_models?.[journal.publishing_model] 
    : undefined;
  const linkTarget = branding?.open_in_new_window ? "_blank" : "_self";
  const multipleIcon = localizedData?.licenses?.["MULTIPLE"] || localizedData?.multiple_licenses_icon;
  const singleLicense = journal.licenses?.length === 1;
  
  const licenseIcon = !journal.licenses?.length ? undefined : 
    singleLicense ? licenseIcons[0] : multipleIcon;
  return (
    <>
      <tr
        className={`resultRow ${
          journal.extra_info?.trim() ? "" : "withBorder"
        }`}
      >
        <td className="journal-name-cell">
          <a
            title={localizedData?.journal_link_tooltip}
            href={generateJournalLink(journal._id || '', slug)}
            target={journal._id ? linkTarget : undefined}
            className="min-h-[44px] min-w-[44px]"
          >
            {journal.name}
          </a>
        </td>
        <td>{journal.imprint}</td>
        <td className="payment-cell">
          {paymentIcon && (
            <a
              className="tooltip min-h-[44px] min-w-[44px]"
              href={generatePaymentLink(journal._id || '', slug)}
              target={journal._id ? linkTarget : undefined}
            >
              <Icon
                icon={paymentIcon}
                width={60}
                height={60}
                alt="Payment option"
              />
              <span className="tooltiptext">{paymentIcon.description?.trim()}</span>
            </a>
          )}
        </td>
        <td className="license-cell">
          {licenseIcon &&
            licenseIcons &&
            (singleLicense ? (
              <LicenseCell 
                icon={licenseIcon} 
                linkTarget={linkTarget} 
                licenseKey={licenseKeys[0]}
                locale={locale}
                slug={slug}
              />
            ) : (
              <MultiLicenseCell 
                icons={licenseIcons} 
                linkTarget={linkTarget}
                multipleIcon={multipleIcon}
                licenseKeys={licenseKeys}
                locale={locale}
                slug={slug}
              />
            ))}
        </td>
        <td className="publishing-model-cell">
          <div className="publishing-model-cell-inner">
            {publishingModelIcon && (
              <a
                className="tooltip min-h-[44px] min-w-[44px]"
                href={generatePublishingModelLink(journal.publishing_model || '', locale, slug)}
                target={journal.publishing_model && locale ? linkTarget : undefined}
              >
                <Icon
                  icon={publishingModelIcon}
                  width={40}
                  height={60}
                  alt="Publishing model"
                />
                <span className="tooltiptext">{publishingModelIcon.description?.trim()}</span>
              </a>
            )}
            <span>{journal.publishing_model}</span>
          </div>
        </td>
      </tr>
      {journal.extra_info && journal.extra_info.trim().length > 0 && (
        <CollapsibleRow
          collapsedTitle={localizedData?.show_notes || "Show notes"}
          expandedTitle={localizedData?.hide_notes || "Hide notes"}
        >
          {journal.extra_info}
        </CollapsibleRow>
      )}
    </>
  );
});

export default Row;
